import graphqlFetch from '@helpers/api/graphql-fetch';
import { generateUrlWithQueryParameter, REMOVE_FALSY } from '@helpers/url';
import { DATO_CONTENT_TYPE, getDatoContent } from '@services/bff-content-hub/datocms';
import { parseSectionFooter, TSectionFooterData } from '@services/datocms/common/getFooter';
import {
  ExhibitionAreaGroup,
  getServerlessProductFlagsData,
} from '@services/datocms/common/getProductFlagsData';
import { getHeaderConfig } from '@services/datocms/header';
import { TInfoBox } from '@services/datocms/product/infobox';
import { getServerlessProductData } from '@services/datocms/product/product';
import { parseHomePageContent, TSections } from '@services/datocms/visual-blocks';
import { gql } from 'graphql-request';
import { campaignQuery, CampaignType, getCampaignVariablesAndFilters } from './getCampaignData';
import { getServerlessHighlightBannerConfigData } from './getHightlightBannerData';
import { getServerlessInfoBoxData } from './getInfoFlag';

const VALID_STATUS = [200];

type MenuTree = NonNullable<any>;

export type GetCommonData<
  T extends {
    menu: any[];
  } = {
    seo?: Record<string, any>;
    menu: MenuTree[];
    ribbonBanner: any;
    productFlagsAreas?: ExhibitionAreaGroup;
    footerData: TSectionFooterData;
    homePageContent?: TSections;
    infoBox?: TInfoBox;
    header?: any;
    campaign?: CampaignType;
  },
> = T;

export type GetCommonDataResult = {
  ribbonbanner: any;
  campaign: CampaignType;
  menu: MenuTree[];
  productFlagsAreas?: ExhibitionAreaGroup;
  footerData: TSectionFooterData;
  seo?: Record<string, any>;
  homePageContent?: TSections;
  productPageConfig?: any;
  infoBox?: TInfoBox;
};

type GetCommonDataProps = {
  preview?: boolean;
  campaignId?: string;
  now?: Date;
  withSections?: boolean;
};

export function getRibbonBannerData(campaign: CampaignType): any {
  if (!campaign?.highlightBanner) {
    return {
      highlightBanner: null,
    };
  }

  try {
    const {
      highlightBanner: { startDate, endDate, clockActive },
    } = campaign;

    const date = new Date().toISOString();
    const hasClock = clockActive && startDate <= date && date <= endDate;

    return {
      highlightBanner: {
        ...campaign.highlightBanner,
        clockActive: hasClock,
      },
    };
  } catch (error) {
    return {
      highlightBanner: null,
    };
  }
}

export async function getCommonData({
  preview = false,
  campaignId = null,
  now = new Date(),
  withSections = false,
}: GetCommonDataProps = {}): Promise<Omit<GetCommonDataResult, 'ribbonbanner'>> {
  const { variablesDefinition, filters } = getCampaignVariablesAndFilters(campaignId !== null);
  const variables: Record<string, any> = { now };
  const commonDataGql = gql`
    query CommonData(${variablesDefinition}) {
      ${campaignQuery({ filters })}
    }
  `;

  const [
    {
      campaign: { homePageContent = null, seo = null, ...campaign },
      homePageConfig,
    },
    { sectionFooter },
    menu,
    header,
    productFlagsAreas,
    { infoBox },
  ] = await Promise.all([
    graphqlFetch(commonDataGql, {
      preview,
      variables,
    }),
    getDatoContent({
      modelApiKey: 'section_footer',
      type: DATO_CONTENT_TYPE.SINGLETON,
      id: 'data',
    }),
    getDatoContent({
      modelApiKey: 'menu',
      type: DATO_CONTENT_TYPE.COLLECTION,
      id: 'data',
    }),
    getHeaderConfig(),
    getServerlessProductFlagsData(),
    getServerlessInfoBoxData({ preview }),
  ]);

  const { productPageConfig } = await getServerlessProductData({ preview });
  const optionalResult: Record<string, any> = {};
  const freightInfoBox = infoBox.filter(
    ({ context }: Record<string, any>) => context === 'free_freight'
  );

  if (withSections) {
    optionalResult.homePageContent = await parseHomePageContent({
      homePageContent,
      homePageConfig,
    });
  }

  return {
    footerData: { sectionFooter: parseSectionFooter({ sectionFooter }) },
    campaign,
    menu,
    productFlagsAreas,
    infoBox: freightInfoBox,
    seo,
    header,
    ...optionalResult,
    ...productPageConfig,
  };
}

export async function getGraphqlCommonData({
  preview,
  campaignId,
  withSections = false,
  bannerPage = '',
}: {
  preview?: boolean;
  campaignId?: string;
  withSections?: boolean;
  bannerPage?: string;
} = {}): Promise<GetCommonData> {
  const {
    campaign,
    footerData,
    menu,
    productFlagsAreas,
    productPageConfig,
    homePageContent,
    seo,
    infoBox,
    header,
  } = await getServerlessCommonData({
    preview,
    campaignId,
    withSections,
  });

  let ribbonBanner = getRibbonBannerData(campaign);

  if (bannerPage !== '') {
    const highlightBanner = await getServerlessHighlightBannerConfigData({
      preview,
      bannerPage,
    });

    if (highlightBanner.highlightBanner) {
      ribbonBanner = highlightBanner;
    }
  }

  return {
    menu,
    ribbonBanner,
    productFlagsAreas,
    footerData,
    header,
    campaign,
    ...(infoBox ? { infoBox } : []),
    ...(homePageContent ? { homePageContent } : {}),
    ...(seo ? { seo } : {}),
    ...(productPageConfig ? { productPageConfig } : {}),
  };
}

export async function getServerlessCommonData({
  preview = false,
  campaignId = null,
  withSections = false,
}: {
  preview: boolean;
  campaignId: string;
  withSections?: boolean;
}): Promise<any> {
  const campaignParam = campaignId ? `${campaignId}` : 'active';
  const getSectionsParams = withSections ? '/sections' : '';

  try {
    const url = generateUrlWithQueryParameter(
      `${
        process.env.NEXT_PUBLIC_HTTP_DOMAIN
      }/api/datocms/common-data/${campaignParam}${getSectionsParams}`,
      {
        preview,
      },
      REMOVE_FALSY
    );

    const response = await fetch(url);

    if (!VALID_STATUS.includes(response.status)) {
      throw new Error(`Invalid response status ${response.status}`);
    }

    return await response.json();
  } catch {
    return getCommonData({
      campaignId: campaignId === 'active' ? null : campaignId,
      withSections,
    });
  }
}
